import { Loading } from '@monorepo/shared/components/Loading';
import { EmptyState } from '@monorepo/shared/componentsV2/EmptyState';
import { Link } from '@monorepo/shared/componentsV2/Link';
import { Main } from '@monorepo/shared/componentsV2/Main';
import { SyncingToAnalyticsIndicator } from '@monorepo/shared/componentsV2/SyncingToAnalyticsIndicator';
import { Table } from '@monorepo/shared/componentsV2/Table/Table';
import { TableColumn } from '@monorepo/shared/componentsV2/Table/types';
import { useHasOrgUpdatePermissionOrSuperAdmin } from '@monorepo/shared/hooks/permissions/useHasPermissions';
import { useResourceTypes } from '@monorepo/shared/hooks/resources/useResourceTypes';
import { lookFor } from '@monorepo/shared/utils/lookFor';
import { format } from 'date-fns';
import { ResourceTypeListResponse } from 'mapistry-shared';
import React, { useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { useResourceTypesSyncingToAnalytics } from '../../hooks/useResourceTypesSyncingToAnalytics';
import { ResourceTypeActionMenu } from './ResourceTypeActionMenu';

const NameAndAnalyticsIndicatorContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const StyledTable = styled(Table)`
  height: 100%;
  padding-bottom: 4rem;
` as typeof Table;

type ViewResourceTypesTableProps = {
  className?: string;
  filterBy: string;
  onAddNew: React.MouseEventHandler;
};

export function ViewResourceTypesTable({
  filterBy,
  onAddNew,
  className,
}: ViewResourceTypesTableProps) {
  const { organizationId } = useParams();
  const canEditOrgSettings =
    useHasOrgUpdatePermissionOrSuperAdmin(organizationId);
  const { resourceTypes, isLoading } = useResourceTypes({
    organizationId,
  });
  const { resourceTypeIdsSyncingToAnalytics = [] } =
    useResourceTypesSyncingToAnalytics({
      organizationId,
    });
  const sortedResourceTypes = useMemo(
    () =>
      resourceTypes
        ? resourceTypes.sort((r1, r2) =>
            r1.name.toLowerCase() > r2.name.toLowerCase() ? 1 : -1,
          )
        : [],
    [resourceTypes],
  );

  const columns: TableColumn<ResourceTypeListResponse>[] = useMemo(
    () => [
      {
        id: 'name',
        header: 'Resource Type',
        /* TODO: Fix this the next time the file is edited */
        /* eslint-disable-next-line react/no-unstable-nested-components */
        contents: (row) => (
          <NameAndAnalyticsIndicatorContainer>
            <Link to={`${row.id}/resources`} color="grey">
              {row.name}
            </Link>
            {resourceTypeIdsSyncingToAnalytics.includes(row.id) && (
              <SyncingToAnalyticsIndicator />
            )}
          </NameAndAnalyticsIndicatorContainer>
        ),
        width: '40%',
      },
      {
        id: 'numResources',
        header: 'Items',
        contents: 'numberOfResources',
        align: 'center',
      },
      {
        id: 'createdAt',
        header: 'Created',
        contents: (row) => format(new Date(row.createdAt), 'MM/dd/yy HH:mm'),
      },
      {
        id: 'actions',
        header: 'Actions',
        /* TODO: Fix this the next time the file is edited */
        /* eslint-disable-next-line react/no-unstable-nested-components */
        contents: (row) =>
          organizationId ? (
            <ResourceTypeActionMenu
              organizationId={organizationId}
              resourceType={row}
            />
          ) : (
            ''
          ),
        align: 'center',
        width: '10%',
      },
    ],
    [organizationId, resourceTypeIdsSyncingToAnalytics],
  );

  const filterRowsBy = useCallback(
    (row) => lookFor(filterBy, row, ['name']),
    [filterBy],
  );

  if (isLoading) {
    return <Loading />;
  }

  const hasResourceTypes = !!sortedResourceTypes?.length;
  const aboutResourceType =
    'Resources help you organize items with static fields that can be used in various ways across Logs and sites.';

  return (
    <Main className={className}>
      {hasResourceTypes ? (
        <StyledTable
          ariaLabel="Resource Types"
          columns={columns}
          rowFilter={filterRowsBy}
          rows={sortedResourceTypes}
        />
      ) : (
        <EmptyState
          actionButtonConfigs={
            canEditOrgSettings
              ? [{ label: 'Create new Resource type', onClick: onAddNew }]
              : []
          }
          primaryText="There are no Resource types configured for this organization."
          secondaryText={
            canEditOrgSettings
              ? aboutResourceType
              : `${aboutResourceType} Contact your organization administrator to configure a Resource type.`
          }
        />
      )}
    </Main>
  );
}
